// widgets/chart/ui/ActiveSparkline.tsx
import {FC, useEffect, useMemo, useRef, useState, useCallback} from 'react';
import styled from 'styled-components';
import {
  chartValue,
  LegendItem,
  PeriodKeyForFill,
  XDomain,
  XTickFormatter,
} from 'widgets/chart/model/types';
import {ChartTooltip, TooltipData, TooltipTextItem} from './ChartTooltip';
import {HOVER_LINE_COLOR} from 'widgets/chart/ui/ActiveColumn.tsx';

const SPARKLINE_ANIM_MS = 700;
const TOOLTIP = {width: 167, height: 96, gap: 8};
const TOOLTIP_FADE_MS = 150;

const TOKENS = {
  color: {
    line: 'var(--accent-default, rgba(255, 79, 18, 1))',
    grid: 'var(--neutral-container-soft, rgba(88, 93, 105, 0.05)',
    axis: 'rgba(0, 0, 0, 0)',
    marks: '#6B8AFF',
    threshold: '#FF4F12',
    text: 'var(--fg-muted, rgba(16, 24, 40, 0.55))',
    hoverLine: HOVER_LINE_COLOR,
  },
  size: {padL: 8, padR: 12, padT: 8, padB: 24, stroke: 2},
  text: {fontSize: 10, fontWeight: 400 as const, lineHeight: 16, letterSpacing: 0.4},
};

const Wrap = styled.div<{w: string | number; h: number | string}>`
  position: relative;
  width: ${({w}) => (typeof w === 'number' ? `${w}px` : w)};
  height: ${({h}) => (typeof h === 'number' ? `${h}px` : h)};
  display: flex;
  flex-direction: column;
`;
const Stage = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;
const Overlay = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
`;
const AxisLabel = styled.div`
  position: absolute;
  transform: translate(-50%, 0);
  color: ${TOKENS.color.text};
  font-size: ${TOKENS.text.fontSize}px;
  font-weight: ${TOKENS.text.fontWeight};
  letter-spacing: ${TOKENS.text.letterSpacing}px;
  line-height: ${TOKENS.text.lineHeight}px;
  white-space: nowrap;
  pointer-events: none;
  text-align: center;
`;
const AxisLabelY = styled(AxisLabel)`
  transform: translate(0, -50%);
  left: calc(100% - ${TOKENS.size.padR - 2}px);
`;
const FadeTooltip = styled.div<{visible: boolean}>`
  opacity: ${({visible}) => (visible ? 1 : 0)};
  transition: opacity ${TOOLTIP_FADE_MS}ms ease;
`;

type Props = {
  data: chartValue[];
  seriesLabel: string;
  width?: string | number;
  height?: number | string;
  marks?: number[];
  thresholds?: number[];
  showAxes?: boolean;
  fillFuture?: boolean;
  periodForFill?: PeriodKeyForFill;
  smoothness?: number;
  padding?: Partial<{left: number; right: number; top: number; bottom: number}>;
  legend?: LegendItem[];
  onLegend?: (items: LegendItem[]) => void;
  tooltipText: TooltipTextItem[];
  xTicks?: number[];
  xTickFormatter?: XTickFormatter;
  xDomain?: XDomain;
  showPoints?: boolean;
  currentDate?: number;
};

function useMeasure() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<{w: number; h: number}>({w: 0, h: 0});
  useEffect(() => {
    if (!ref.current) return;
    const obs = new ResizeObserver((entries) => {
      const cr = entries[0].contentRect;
      setRect({w: cr.width, h: cr.height});
    });
    obs.observe(ref.current);
    const r = ref.current.getBoundingClientRect();
    setRect({w: r.width, h: r.height});
    return () => obs.disconnect();
  }, []);
  return {ref, rect};
}

function inferXUnit(stepMs: number): 'hour' | 'day' | 'week' | 'month' {
  const h = 3600_000;
  const d = 24 * h;
  if (stepMs <= 2 * h) return 'hour';
  if (stepMs <= 2 * d) return 'day';
  if (stepMs <= 14 * d) return 'week';
  return 'month';
}
function periodMsByKey(k: PeriodKeyForFill): number {
  const h = 3600_000;
  const d = 24 * h;
  if (k === 'day') return d;
  if (k === 'week') return 7 * d;
  return 30 * d;
}
const ddmm = (d: Date) =>
  `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
const hhmm = (d: Date) =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
const isSameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

function placeTooltip(x: number, yTop: number, viewWidth: number) {
  const minLeft = 0;
  const maxLeft = Math.max(0, viewWidth - TOOLTIP.width);
  const rawLeft = x - TOOLTIP.width / 2;
  const left = Math.min(Math.max(rawLeft, minLeft), maxLeft);
  let arrowX = x - left;
  arrowX = Math.max(6, Math.min(TOOLTIP.width - 6, arrowX));
  const top = yTop - TOOLTIP.height - TOOLTIP.gap;
  return {left, top, arrowX};
}

export const ActiveSparkline: FC<Props> = ({
  data,
  seriesLabel,
  width = '100%',
  height = '100%',
  marks,
  thresholds,
  showAxes = true,
  fillFuture = false,
  periodForFill = 'day',
  smoothness = 1,
  padding,
  legend,
  onLegend,
  tooltipText,
  xTicks: externalXTicks,
  xTickFormatter,
  xDomain,
  showPoints = false,
  currentDate,
}) => {
  const outer = useMeasure();

  const padL = padding?.left ?? (showAxes ? TOKENS.size.padL : 4);
  const padR = padding?.right ?? TOKENS.size.padR;
  const padT = padding?.top ?? TOKENS.size.padT;
  const padB = padding?.bottom ?? TOKENS.size.padB;

  const vw = Math.max(1, outer.rect.w || 600);
  const vh = Math.max(1, outer.rect.h || (typeof height === 'number' ? height : 180));

  const real = data.filter((d) => d.y != null);
  const xsAll = real.map((d) => d.x);
  const ysReal = real.map((d) => d.y as number);

  const minXData = xsAll.length ? Math.min(...xsAll) : 0;
  const stepX = real.length > 1 ? real[1].x - real[0].x : 3600_000;
  const xUnit = inferXUnit(stepX);
  const maxXData = xsAll.length ? Math.max(...xsAll) : minXData;
  const desiredEnd = fillFuture ? minXData + periodMsByKey(periodForFill) : maxXData;
  const isCurrentDateMode = currentDate !== undefined;
  const [minXFixed, maxXFixed] = useMemo(() => {
    if (isCurrentDateMode) {
      const start24h = (currentDate as number) - 24 * 3600_000;
      return [start24h, currentDate as number];
    }
    const minX = xDomain?.min ?? minXData;
    const maxX = xDomain?.max ?? Math.max(maxXData, desiredEnd);
    return [minX, maxX];
  }, [isCurrentDateMode, currentDate, xDomain?.min, xDomain?.max, minXData, maxXData, desiredEnd]);

  const realForRender = useMemo(
    () => (isCurrentDateMode ? real.filter((d) => d.x >= minXFixed && d.x <= maxXFixed) : real),
    [isCurrentDateMode, real, minXFixed, maxXFixed]
  );

  const avgY = ysReal.reduce((a, b) => a + b, 0) / (ysReal.length || 1);
  let minY = Math.min(...(ysReal.length ? ysReal : [0])) - avgY * 0.005;
  let maxY = Math.max(...(ysReal.length ? ysReal : [1])) + avgY * 0.005;
  if (!(isFinite(minY) && isFinite(maxY)) || minY === maxY) {
    minY = minY - 1;
    maxY = maxY + 1;
  }

  const innerW = vw - padL - padR;
  const innerH = vh - padT - padB;

  const xScale = (x: number) => {
    const scaled = padL + ((x - minXFixed) / (maxXFixed - minXFixed || 1)) * innerW;
    // Ограничиваем позицию в пределах видимой области
    return Math.max(padL, Math.min(scaled, vw - padR));
  };
  const yScale = (y: number) => vh - padB - ((y - minY) / (maxY - minY || 1)) * innerH;

  const path = useMemo(() => {
    if (showPoints) {
      if (realForRender.length === 0) return '';
      const pts = realForRender.map((d) => ({X: xScale(d.x), Y: yScale(d.y as number)}));
      if (pts.length === 1) return `M ${pts[0].X} ${pts[0].Y}`;
      let d = `M ${pts[0].X} ${pts[0].Y}`;
      for (let i = 1; i < pts.length; i++) {
        d += ` L ${pts[i].X} ${pts[i].Y}`;
      }
      return d;
    } else {
      if (realForRender.length === 0) return '';
      const pts = realForRender.map((d) => ({X: xScale(d.x), Y: yScale(d.y as number)}));
      if (pts.length === 1) return `M ${pts[0].X} ${pts[0].Y}`;
      let d = `M ${pts[0].X} ${pts[0].Y}`;
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] ?? pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] ?? p2;
        const c1x = p1.X + ((p2.X - p0.X) / 6) * smoothness;
        const c1y = p1.Y + ((p2.Y - p0.Y) / 6) * smoothness;
        const c2x = p2.X - ((p3.X - p1.X) / 6) * smoothness;
        const c2y = p2.Y - ((p3.Y - p1.Y) / 6) * smoothness;
        d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.X} ${p2.Y}`;
      }
      return d;
    }
  }, [
    realForRender,
    minXFixed,
    maxXFixed,
    minY,
    maxY,
    padL,
    padR,
    padT,
    padB,
    innerW,
    innerH,
    showPoints,
  ]);

  const [tip, setTip] = useState<{
    left: number;
    top: number;
    arrowX: number;
    xPix: number;
    data: TooltipData;
  } | null>(null);
  const [visible, setVisible] = useState(false);
  const svgRef = useRef<SVGSVGElement | null>(null);

  const onMouseMove = (e: React.MouseEvent) => {
    if (!showAxes || realForRender.length === 0) return;
    const rect = (svgRef.current as SVGSVGElement).getBoundingClientRect();
    const xp = e.clientX - rect.left;
    const xVal = ((xp - padL) / (innerW || 1)) * (maxXFixed - minXFixed) + minXFixed;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < realForRender.length; i++) {
      const d = Math.abs(realForRender[i].x - xVal);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    const target = realForRender[best];
    const xPix = xScale(target.x);
    const yPix = yScale(target.y as number);
    const pos = placeTooltip(xPix, yPix, vw);
    // Форматируем значение в зависимости от типа графика
    const title = tooltipText?.[0]?.title?.toLowerCase() || '';
    const dim = tooltipText?.[0]?.dim || '';
    let formattedValue: number = target.y as number;

    // Влажность: 0 знаков после запятой (полное округление до целого)
    if (title.includes('влажность') || dim === '%') {
      formattedValue = Math.round(target.y as number);
    }
    // Температура: 1 знак после запятой
    else if (title.includes('температура') || dim === '°C') {
      formattedValue = Number((target.y as number).toFixed(1));
    }
    // По умолчанию: 2 знака после запятой
    else {
      formattedValue = Number((target.y as number).toFixed(2));
    }

    setTip({
      left: pos.left,
      top: pos.top,
      arrowX: pos.arrowX,
      xPix,
      data: {type: 'sparkline', current: {ts: target.x, value: formattedValue}, tooltipText},
    });
    setVisible(true);
  };

  const onMouseLeave = () => {
    setVisible(false);
    setTimeout(() => setTip(null), TOOLTIP_FADE_MS);
  };

  const yTicks = useMemo(() => {
    const res: number[] = [];
    const step = (maxY - minY) / 5;
    for (let i = 0; i <= 5; i++) res.push(minY + i * step);
    return res;
  }, [minY, maxY]);

  const autoXTicks = useMemo(() => {
    const tickCount = 5;
    const step = (maxXFixed - minXFixed) / (tickCount - 1 || 1);
    return Array.from({length: tickCount}, (_, i) => minXFixed + i * step);
  }, [minXFixed, maxXFixed]);

  const xTicks = useMemo(() => {
    if (isCurrentDateMode) {
      const start = minXFixed;
      const end = maxXFixed;
      const ticks: number[] = [start];

      const nextSixHourBoundary = (ts: number) => {
        const d = new Date(ts);
        d.setHours(Math.floor(d.getHours() / 6) * 6, 0, 0, 0);
        if (d.getTime() <= ts) d.setHours(d.getHours() + 6);
        return d.getTime();
      };

      let t = nextSixHourBoundary(start);
      while (t < end) {
        ticks.push(t);
        t += 6 * 3600_000;
      }
      if (ticks[ticks.length - 1] !== end) ticks.push(end);
      return ticks;
    }

    if (externalXTicks && externalXTicks.length > 1) return externalXTicks;

    if (real.length === 0) return autoXTicks;

    // Всегда начинаем с первого значения данных
    const firstX = real[0].x;
    const lastX = maxXFixed;

    // Для часовых данных показываем больше меток
    const tickCount = xUnit === 'hour' ? 6 : 5;

    const step = (lastX - firstX) / (tickCount - 1 || 1);

    // Создаем метки, начиная с первого значения
    const ticks = Array.from({length: tickCount}, (_, i) => {
      return firstX + i * step;
    });

    // Убедимся, что первая метка точно равна первому значению данных
    ticks[0] = firstX;

    // Убедимся, что последняя метка не выходит за границы
    ticks[ticks.length - 1] = Math.min(ticks[ticks.length - 1], lastX);

    return ticks.filter((x) => x >= minXFixed && x <= maxXFixed);
  }, [isCurrentDateMode, externalXTicks, real, minXFixed, maxXFixed, autoXTicks, xUnit]);
  const fmtXParts = useCallback(
    (ms: number) => {
      const d = new Date(ms);
      const today = new Date();
      if (xUnit === 'hour') {
        if (isSameDay(d, today)) return [hhmm(d)];
        return [`${hhmm(d)}`, ddmm(d)];
      }
      if (xUnit === 'week') {
        const start = new Date(d.getTime());
        const end = new Date(start.getTime() + 6 * 24 * 3600_000);
        return [`${ddmm(start)}`, `${ddmm(end)}`];
      }
      return [ddmm(d)];
    },
    [xUnit]
  );

  const formatTick = useCallback(
    (value: number, index: number, ticks: number[]) => {
      if (isCurrentDateMode) return [hhmm(new Date(value))];
      if (xTickFormatter) return xTickFormatter(value, index, ticks);
      return fmtXParts(value);
    },
    [isCurrentDateMode, xTickFormatter, fmtXParts]
  );
  const xLabels = useMemo(() => {
    const minGap = 6;
    const labels = xTicks.map((t, i) => {
      const parts = formatTick(t, i, xTicks);
      const text = parts.join(' ');
      const width = Math.max(22, text.length * (TOKENS.text.fontSize * 0) + 8);
      const half = width / 2;
      const rawX = xScale(t);
      const minX = padL + half;
      const maxX = vw - padR - half;
      const x = Math.max(minX, Math.min(rawX, maxX));
      return {t, parts, half, x, minX, maxX};
    });

    if (labels.length <= 2) {
      if (labels.length >= 1) labels[0].x = xScale(labels[0].t);
      if (labels.length === 2) labels[1].x = xScale(labels[1].t);
      return labels;
    }

    const lastIdx = labels.length - 1;
    const firstFixed = xScale(labels[0].t);
    const lastFixed = xScale(labels[lastIdx].t);

    // 1) Сдвиг промежуточных меток вправо от левой границы.
    for (let i = 1; i < lastIdx; i++) {
      const prev = labels[i - 1];
      const cur = labels[i];
      const minCenter = prev.x + prev.half + minGap + cur.half;
      if (cur.x < minCenter) cur.x = minCenter;
      if (cur.x > cur.maxX) cur.x = cur.maxX;
    }

    // 2) Сдвиг промежуточных меток влево от правой границы.
    for (let i = lastIdx - 1; i >= 1; i--) {
      const next = labels[i + 1];
      const cur = labels[i];
      const maxCenter = next.x - next.half - minGap - cur.half;
      if (cur.x > maxCenter) cur.x = maxCenter;
      if (cur.x < cur.minX) cur.x = cur.minX;
    }

    // 3) Фиксируем первую и последнюю на исходной позиции.
    labels[0].x = firstFixed;
    labels[lastIdx].x = lastFixed;

    return labels;
  }, [xTicks, formatTick, xScale, padL, padR, vw]);

  const fmtY = (v: number) => {
    // Определяем тип графика по tooltipText для влажности
    const title = tooltipText?.[0]?.title?.toLowerCase() || '';
    const dim = tooltipText?.[0]?.dim || '';

    // Влажность: 0 знаков после запятой (полное округление до целого)
    if (title.includes('влажность') || dim === '%') {
      return Math.round(v).toString();
    }

    // Температура: 1 знак после запятой
    if (title.includes('температура') || dim === '°C') {
      return v.toFixed(1);
    }

    // По умолчанию используем стандартную логику
    const range = Math.abs(maxY - minY);
    return range >= 10 ? Math.round(v).toString() : v.toFixed(1);
  };

  const [animT, setAnimT] = useState(0);
  const clipId = useRef(`clip-${Math.random().toString(36).slice(2)}`).current;
  const gradId = useRef(`grad-${Math.random().toString(36).slice(2)}`).current;
  const animatedOnce = useRef(false);

  useEffect(() => {
    if (animatedOnce.current) {
      setAnimT(1);
      return;
    }
    let raf = 0;
    const t0 = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(1, (now - t0) / SPARKLINE_ANIM_MS);
      setAnimT(dt);
      if (dt < 1) raf = requestAnimationFrame(loop);
      else animatedOnce.current = true;
    };
    setAnimT(0);
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    setTip(null);
    setVisible(false);
  }, [data]);

  const [pathLen, setPathLen] = useState(1);
  const pathRef = useRef<SVGPathElement | null>(null);
  useEffect(() => {
    if (pathRef.current) setPathLen(pathRef.current.getTotalLength() || 1);
  }, [path, vw, vh]);

  const clipWidth = useMemo(() => {
    if (isCurrentDateMode) {
      return padL + animT * innerW;
    }
    const latest = xsAll.length ? Math.min(xsAll[xsAll.length - 1], maxXFixed) : minXFixed;
    const target = padL + ((latest - minXFixed) / (maxXFixed - minXFixed || 1)) * innerW;
    return padL + animT * (target - padL);
  }, [isCurrentDateMode, animT, minXFixed, maxXFixed, innerW, padL, xsAll]);

  const legendAuto: LegendItem[] = useMemo(() => {
    const base: LegendItem[] = [{color: TOKENS.color.line, label: seriesLabel}];
    const markItems = (marks ?? []).map((v) => ({color: TOKENS.color.marks, label: `Метка ${v}`}));
    const extra = legend ?? [];
    return [...base, ...markItems, ...extra];
  }, [seriesLabel, marks, legend]);

  useEffect(() => {
    if (onLegend) onLegend(legendAuto);
  }, [onLegend, legendAuto]);

  const visibleMarks = useMemo(
    () => (marks ?? []).filter((m) => m >= minY && m <= maxY),
    [marks, minY, maxY]
  );
  const visibleThresholds = useMemo(
    () => (thresholds ?? []).filter((t) => t >= minY && t <= maxY),
    [thresholds, minY, maxY]
  );

  return (
    <Wrap w={width} h={height}>
      <Stage ref={outer.ref}>
        <svg
          ref={svgRef}
          width={vw}
          height={vh}
          style={{display: 'block'}}
          onMouseMove={showAxes ? onMouseMove : undefined}
          onMouseLeave={showAxes ? onMouseLeave : undefined}
        >
          <defs>
            <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
              <rect x="0" y="0" width={clipWidth} height={vh} />
            </clipPath>
            <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
              <stop
                offset="0%"
                stopColor={'var(--accent-default, rgba(255, 79, 18, 1))'}
                stopOpacity="0.10"
              />
              <stop
                offset="100%"
                stopColor={'var(--accent-default, rgba(255, 79, 18, 1))'}
                stopOpacity="0"
              />
            </linearGradient>
          </defs>

          {showAxes && (
            <>
              <line
                x1={padL}
                x2={vw - padR}
                y1={vh - padB}
                y2={vh - padB}
                stroke={TOKENS.color.axis}
                strokeWidth={1}
              />
              {yTicks.map((t, i) => (
                <line
                  key={`yg-${i}`}
                  x1={padL}
                  x2={vw - padR}
                  y1={yScale(t)}
                  y2={yScale(t)}
                  stroke={TOKENS.color.grid}
                  strokeWidth={1}
                />
              ))}
              {xTicks.map((t, i) => {
                // const isLast = i === xTicks.length - 1;
                // if (isLast) return null;
                const x = xScale(t);
                return (
                  <line
                    key={`xg-${i}`}
                    x1={x}
                    x2={x}
                    y1={padT}
                    y2={vh - padB}
                    stroke={TOKENS.color.grid}
                    strokeWidth={1}
                  />
                );
              })}
            </>
          )}

          <g clipPath={`url(#${clipId})`}>
            <path
              ref={pathRef}
              d={path}
              fill="none"
              stroke={'var(--accent-default, rgba(255, 79, 18, 1))'}
              strokeWidth={TOKENS.size.stroke}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                strokeDasharray: pathLen,
                strokeDashoffset: (1 - animT) * pathLen,
                transition: `stroke-dashoffset ${SPARKLINE_ANIM_MS}ms ease`,
              }}
            />
            {fillFuture && realForRender.length > 0 && (
              <path
                d={`${path} L ${xScale(realForRender[realForRender.length - 1].x)} ${vh - padB} L ${xScale(realForRender[0].x)} ${vh - padB} Z`}
                fill={`url(#${gradId})`}
              />
            )}
          </g>

          {visibleMarks.map((m, i) => (
            <line
              key={`m-${i}`}
              x1={padL}
              x2={vw - padR}
              y1={yScale(m)}
              y2={yScale(m)}
              stroke={TOKENS.color.marks}
              strokeDasharray="6 6"
              strokeWidth={1}
            />
          ))}

          {visibleThresholds.map((t, i) => (
            <line
              key={`thr-${i}`}
              x1={padL}
              x2={vw - padR}
              y1={yScale(t)}
              y2={yScale(t)}
              stroke={TOKENS.color.threshold}
              strokeWidth={1}
            />
          ))}

          {showAxes && tip && (
            <line
              x1={tip.xPix}
              x2={tip.xPix}
              y1={padT}
              y2={vh - padB}
              stroke={TOKENS.color.hoverLine}
              strokeWidth={1}
              strokeDasharray="4 2"
            />
          )}
          {showAxes && tip && (
            <line
              x1={tip.xPix}
              x2={tip.xPix}
              y1={padT}
              y2={vh - padB}
              stroke={TOKENS.color.hoverLine}
              strokeWidth={1}
              strokeDasharray="4 2"
            />
          )}
          {/* Отображение точек данных */}
          {showPoints &&
            realForRender.map((point, i) => {
              const x = xScale(point.x);
              const y = yScale(point.y as number);
              return (
                <circle
                  key={`point-${i}`}
                  cx={x}
                  cy={y}
                  r={3}
                  fill={'var(--accent-default, rgba(255, 79, 18, 1))'}
                />
              );
            })}
        </svg>

        <Overlay>
          {showAxes &&
            yTicks.map((t, i) => (
              <AxisLabelY key={`yl-${i}`} style={{top: yScale(t)}}>
                {fmtY(t)}
              </AxisLabelY>
            ))}
          {showAxes &&
            xLabels.map((label, i) => {
              return (
                <AxisLabel key={`xl-${i}`} style={{left: label.x, top: vh - padB + 2}}>
                  {
                    <>
                      {label.parts[0]}
                      {label.parts[1] ? (
                        <>
                          <br />
                          {label.parts[1]}
                        </>
                      ) : null}
                    </>
                  }
                </AxisLabel>
              );
            })}
          {showAxes && tip && (
            <FadeTooltip visible={visible}>
              <ChartTooltip
                data={tip.data}
                tooltipText={tooltipText}
                style={{left: tip.left, top: tip.top, ['--arrow-x']: `${tip.arrowX}px`}}
              />
            </FadeTooltip>
          )}
        </Overlay>
      </Stage>
    </Wrap>
  );
};
